<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { BaseSelectBox } from '@cplug/design-system/components'
import { BaseSelectBoxSize, BaseSelectBoxVariant } from '@cplug/design-system/components/props'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { useMyPlanStore } from '@cplug/frontend-subscription-hub/domain/plans/my-plan'
import { useHeaderCompanySelectSearch } from '../composables/useHeaderCompanySelectSearch'
import { useTranslation } from './BaseHeaderRightSide.i18n'
import { useToast } from '~/shared/composables/useToast'
import type { Company } from '~/domain/shared/company/models/Company'

const { t } = useTranslation()
const toast = useToast()

const authStore = useAuthenticationStore()
const { companies: selectedCompanies } = storeToRefs(authStore)
const myPlanStore = useMyPlanStore()

const {
  selectOptions,
  isRemoteSearchLoading,
  companies,
  pageMeta,
  onFilter,
  onDropdownHide,
  ensureInitialLoad,
  findCompanyById
} = useHeaderCompanySelectSearch()

const selectedCompany = ref<Company | undefined>(selectedCompanies.value[0])
const hasMultipleCompanies = ref(false)
const isInitialLoading = ref(true)

function updateHasMultipleCompanies (): void {
  const isMultiple = (pageMeta.value?.total ?? 0) > 1 || (companies.value?.length ?? 0) > 1
  if (isMultiple) {
    hasMultipleCompanies.value = true
  }
}

async function initCompanies (): Promise<void> {
  const loaded = await ensureInitialLoad()
  isInitialLoading.value = false

  if (!loaded) {
    toast.error(t('ErrorLoadingCompanies'))
    return
  }

  updateHasMultipleCompanies()
}

initCompanies()

watch([companies, pageMeta], updateHasMultipleCompanies)

const shouldShowSelect = computed(() => hasMultipleCompanies.value || isInitialLoading.value)

const selectedCompanyValue = computed({
  get: () => selectedCompany.value?.id,
  set: (newCompanyId: number | undefined) => {
    if (newCompanyId) {
      const company = findCompanyById(newCompanyId)
      if (company) {
        selectedCompany.value = company
        authStore.setCompanies([company])
        myPlanStore.model = undefined

        const currentRoute = window.location.pathname
        const blockedRoutes = ['/dashboard/blocked-plan', '/dashboard/overdue-payment']

        if (blockedRoutes.includes(currentRoute)) {
          window.location.href = '/dashboard'
          return
        }
        window.location.reload()
      }
    } else {
      selectedCompany.value = undefined
      authStore.setCompanies([])
    }
  }
})

watch(() => companies.value, (loadedCompanies) => {
  const currentAuthCompany = selectedCompanies.value[0]
  if (!currentAuthCompany?.id || currentAuthCompany.contract) {
    return
  }

  const fullCompany = loadedCompanies?.find(company => company.id === currentAuthCompany.id)
  if (fullCompany) {
    authStore.setCompanies([fullCompany])
    selectedCompany.value = fullCompany
  }
})

function onSearchCompanies (value: string): void {
  onFilter(value)
}
</script>

<template>
  <BaseSelectBox
    v-if="shouldShowSelect"
    v-model="selectedCompanyValue"
    class="w-full min-w-0"
    :clearable="false"
    :options="selectOptions"
    :placeholder="t('SelectUnit')"
    :filter="true"
    :filter-placeholder="t('SearchCompanies')"
    :empty-filter-message="t('NoCompaniesFound')"
    :loading="isRemoteSearchLoading || isInitialLoading"
    :on-search="onSearchCompanies"
    :reset-filter-on-hide="true"
    :size="BaseSelectBoxSize.Small"
    :sorted="false"
    :variant="BaseSelectBoxVariant.Contained"
    data-testid="sidebar-company-select"
    @hide="onDropdownHide"
  />
</template>
